import type { CSSProperties } from "react";
import {
	DSFont,
	Skeleton,
} from "@/components/design-system/ArtistDesignSystem";
import type { DesignerPromptSuggestion } from "./genArtCatalog";

export interface GenArtArtworkCardProps {
	readonly suggestion: DesignerPromptSuggestion;
	readonly onSelect: (suggestion: DesignerPromptSuggestion) => void;
}

const cardFrameStyle: CSSProperties = {
	position: "relative",
	width: "216px",
	height: "118px",
	flex: "0 0 216px",
	scrollSnapAlign: "start",
	borderRadius: "11px",
	overflow: "hidden",
	border: "1px solid color-mix(in srgb, var(--ds-divider) 70%, transparent)",
	cursor: "pointer",
	padding: 0,
	background: "transparent",
	textAlign: "left",
};

/**
 * GenArtArtworkCard — 216×118 suggestion card. Mirrors iOS
 * `GenArtArtworkCard`: brand gradient, bottom black gradient, single-line
 * caption (genArtRegular 12px, white 95%). Tapping the card writes the
 * suggestion prompt into the textarea.
 */
export function GenArtArtworkCard({
	suggestion,
	onSelect,
}: Readonly<GenArtArtworkCardProps>) {
	return (
		<button
			type="button"
			style={cardFrameStyle}
			aria-label={suggestion.caption}
			onClick={() => onSelect(suggestion)}
		>
			{suggestion.artworkURL ? (
				<img
					src={suggestion.artworkURL}
					alt=""
					aria-hidden="true"
					style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
				/>
			) : (
				<span
					aria-hidden="true"
					style={{
						position: "absolute",
						inset: 0,
						background: suggestion.artworkGradient,
					}}
				/>
			)}
			<span
				aria-hidden="true"
				style={{
					position: "absolute",
					inset: 0,
					background:
						"linear-gradient(to bottom, rgb(0 0 0 / 0) 35%, rgb(0 0 0 / 0.72) 100%)",
				}}
			/>
			<span
				style={{
					position: "absolute",
					left: "8px",
					right: "8px",
					bottom: "6px",
					font: DSFont.genArtRegular(12),
					color:
						"color-mix(in srgb, var(--ds-on-media-primary) 95%, transparent)",
					lineHeight: 1.25,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{suggestion.caption}
			</span>
		</button>
	);
}

export interface GenArtArtworkPlaceholderCardProps {
	readonly caption: string;
	readonly isLoading: boolean;
}

/**
 * GenArtArtworkPlaceholderCard — loading / failed-state variant of the
 * suggestion card. Mirrors iOS `GenArtArtworkPlaceholderCard`.
 */
export function GenArtArtworkPlaceholderCard({
	caption,
	isLoading,
}: Readonly<GenArtArtworkPlaceholderCardProps>) {
	return (
		<div aria-hidden="true" style={{ ...cardFrameStyle, cursor: "default" }}>
			<span
				aria-hidden="true"
				style={{
					position: "absolute",
					inset: 0,
					background:
						"linear-gradient(135deg, color-mix(in srgb, var(--ds-background) 95%, transparent), color-mix(in srgb, var(--ds-surface) 95%, transparent))",
				}}
			/>
			{isLoading ? (
				<span
					style={{
						position: "absolute",
						inset: 0,
						padding: 0,
					}}
				>
					<Skeleton shape="rectangle" width="100%" height="118px" />
				</span>
			) : null}
			<span
				aria-hidden="true"
				style={{
					position: "absolute",
					inset: 0,
					background:
						"linear-gradient(to bottom, rgb(0 0 0 / 0) 35%, rgb(0 0 0 / 0.72) 100%)",
				}}
			/>
			<span
				style={{
					position: "absolute",
					left: "8px",
					right: "8px",
					bottom: "6px",
					font: DSFont.genArtRegular(12),
					color:
						"color-mix(in srgb, var(--ds-on-media-primary) 95%, transparent)",
					lineHeight: 1.25,
				}}
			>
				{caption}
			</span>
		</div>
	);
}
